import React from 'react';
import ReactMarkdown from 'react-markdown';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { InputMessage, ModelResponse } from 'types/open_ai';

import { useCreateModelResponse } from 'hooks/useCreateModelResponse';
import { usePipeline } from 'hooks/api/usePipeline';

import { Button } from 'components/Button/Button';
import { CopyToClipboardButton } from 'components/CopyToClipboardButton/CopyToClipboardButton';
import { FormMessage } from 'components/FormMessage/FormMessage';
import { Heading } from 'components/Heading/Heading';
import { IconButton } from 'components/IconButton/IconButton';
import { Pill } from 'components/Pill/Pill';
import { Textarea } from 'components/Textarea/Textarea';

const suggestions = [
  'Explain this code to me',
  'Edit this code to also display current time on the frame',
  'Write code that displays current object counts from the prior line counter node on the frame',
];

type AdjacencyPair = { input: InputMessage; response: ModelResponse | null };
type Conversation = Array<AdjacencyPair & { key: string }>;

type AssistantProps = { functionNodeCode: string | undefined };

export function Assistant({ functionNodeCode = '' }: AssistantProps) {
  const { pipelineID } = useParams<{ pipelineID: string }>();
  const { data: pipeline } = usePipeline(pipelineID);

  const [conversation, setConversation] = React.useState<Conversation>([]);
  const conversationRef = React.useRef<HTMLDivElement>(null);

  const {
    data: modelResponse,
    mutate: createModelResponse,
    reset: resetModelResponse,
    isLoading,
  } = useCreateModelResponse({
    onSuccess,
  });

  const { getValues, register, watch, setFocus, reset } = useForm<{
    prompt: string;
  }>();

  React.useEffect(() => {
    conversationRef.current?.scrollTo({
      top: conversationRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [conversation.length, setFocus]);

  function onSuccess(response: ModelResponse) {
    setConversation((previousState) =>
      previousState.map((pair, index) =>
        index === previousState.length - 1 && pair.response === null
          ? { ...pair, response }
          : pair
      )
    );

    reset({ prompt: '' });

    setTimeout(() => {
      setFocus('prompt');
    }, 300);
  }

  function handlePromptSubmit(prompt: string) {
    if (!prompt || !pipeline) return;

    const userInput: InputMessage = {
      role: 'user',
      content: [
        {
          type: 'input_text',
          text: prompt,
        },
      ],
    };

    const input = [userInput];

    // Attach pipeline definition & function node code on first prompt
    if (conversation.length === 0) {
      if (functionNodeCode) {
        input.unshift({
          role: 'user',
          content: [
            {
              type: 'input_text',
              text: `Current function node code is: ${functionNodeCode}`,
            },
          ],
        });
      }

      // Remove extension field only relevant for console
      const definition = pipeline.definition.map(({ extension, ...node }) => {
        if (node.properties && 'code' in node.properties) {
          // Remove function node code which is already attached
          const { code, ...properties } = node.properties;
          return {
            ...node,
            properties,
          };
        }
        return node;
      });

      input.unshift({
        role: 'user',
        content: [
          {
            type: 'input_text',
            text: `Pipeline graph is: ${JSON.stringify(definition)}`,
          },
        ],
      });
    }

    setConversation((previousState) => [
      ...previousState,
      { key: crypto.randomUUID(), input: userInput, response: null },
    ]);

    createModelResponse({
      input,
      model: 'gpt-4o',
      previous_response_id: modelResponse ? modelResponse.id : null,
    });
  }

  function handleClearConversation() {
    setConversation([]);
    resetModelResponse();
    setFocus('prompt');
  }

  const hasConversation = conversation.length > 0;

  return (
    <div className="relative flex h-full w-full flex-col justify-between rounded-md border-1 border-neutral-200 dark:border-neutral-700">
      {hasConversation ? (
        <div
          ref={conversationRef}
          className="flex flex-col gap-3 overflow-auto p-3 text-sm"
        >
          {conversation.map(({ key, input, response }) => (
            <React.Fragment key={key}>
              <UserMessage input={input} />
              <AssistantMessage response={response} isLoading={isLoading} />
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center">
          <Heading level="4">
            Lumeo Assistant <Pill>Beta</Pill>
          </Heading>
          <span>Edit, review or explain code with generative AI</span>
        </div>
      )}

      <div className="flex flex-col gap-2 p-3">
        {!hasConversation && (
          <div className="flex flex-col gap-1">
            <FormMessage intent="info" icon="info">
              Lumeo Assistant is aware of methods supported in this custom
              function and your pipeline context. You can reference specific
              nodes or node id’s in your chat.
            </FormMessage>
            <span>Try the following suggestions to get started:</span>
            {suggestions.map((prompt) => (
              <div
                key={prompt}
                style={{ '--fw-display': '400' } as React.CSSProperties}
              >
                <Button
                  onClick={() => handlePromptSubmit(prompt)}
                  size="xsmall"
                >
                  {prompt}
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="flex h-30 w-full items-center gap-3">
          <Textarea
            className="h-full resize-none"
            placeholder={
              hasConversation
                ? undefined
                : 'Ask about your function node code...'
            }
            disabled={isLoading}
            {...register('prompt')}
            autoFocus
          />

          <div className="flex flex-col items-center justify-center gap-2">
            <IconButton
              icon="new-chat"
              label="Clear and create new chat"
              variant="ghost"
              onClick={handleClearConversation}
              disabled={conversation.length === 0}
            />
            <IconButton
              icon="arrow-up"
              label="Send message"
              variant="primary"
              onClick={() => handlePromptSubmit(getValues('prompt'))}
              disabled={!watch('prompt') || isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function UserMessage({ input }: { input: InputMessage }) {
  const [content] = input.content;

  if (!('text' in content)) {
    return null;
  }

  return (
    <div>
      <Role>User</Role>
      <div className="py-2">{content.text}</div>
    </div>
  );
}

function AssistantMessage({
  response,
  isLoading,
}: {
  response: ModelResponse | null;
  isLoading: boolean;
}) {
  if (response === null && isLoading) {
    return (
      <div>
        <Role>Assistant</Role>
        <DotsLoader />
      </div>
    );
  }

  if (!response) {
    return null;
  }

  if (response.error !== null) {
    return (
      <div>
        <Role>Assistant</Role>
        <FormMessage icon="warning" intent="danger">
          {response.error.message}
        </FormMessage>
      </div>
    );
  }

  const [output] = response.output.filter(({ type }) => type === 'message');

  if (!output || output.type !== 'message') {
    return null;
  }

  const [content] = output.content;

  if (!content) {
    return null;
  }

  if (content.type === 'refusal') {
    return (
      <div>
        <Role>Assistant</Role>
        <div className="py-2">{content.refusal}</div>
      </div>
    );
  }

  return (
    <div>
      <Role>Assistant</Role>
      <div className="py-2">
        <ReactMarkdown
          components={{
            code: Code,
            h1: Title,
            h2: Title,
            h3: Title,
            h4: Title,
            h5: Title,
            h6: Title,
            li: Li,
            ol: Ol,
            pre: Pre,
            ul: Ul,
          }}
        >
          {content.text}
        </ReactMarkdown>
      </div>
    </div>
  );
}

function Role({ children }: React.PropsWithChildren) {
  return <Heading level="4">{children}</Heading>;
}

function Pre({ children }: React.PropsWithChildren) {
  const text = React.useMemo(() => {
    if (
      React.isValidElement(children) &&
      typeof children.props.children === 'string'
    ) {
      return children.props.children;
    }
  }, [children]);

  return (
    <div className="relative">
      <pre className="my-3 overflow-hidden overflow-x-auto rounded-md bg-neutral-100 p-2 dark:bg-neutral-700">
        {text && typeof text === 'string' && (
          <CopyToClipboardButton
            className="absolute top-2 right-2 z-5"
            text={text}
          />
        )}
        {children}
      </pre>
    </div>
  );
}

function Code({ children }: React.PropsWithChildren) {
  return (
    <code className="px-1 py-0.5 text-xs [&:not(pre>code)]:rounded-sm [&:not(pre>code)]:bg-neutral-100 [&:not(pre>code)]:dark:bg-neutral-700">
      {children}
    </code>
  );
}

function Li({ children }: React.PropsWithChildren) {
  return <li className="p-1">{children}</li>;
}

function Ul({ children }: React.PropsWithChildren) {
  return <ul className="!ml-3 !list-disc !p-3">{children}</ul>;
}

function Ol({ children }: React.PropsWithChildren) {
  return <ol className="!ml-3 !list-decimal !p-3">{children}</ol>;
}

function Title({ children }: React.PropsWithChildren) {
  return (
    <Heading className="!my-1" level="4">
      {children}
    </Heading>
  );
}

function DotsLoader() {
  return (
    <div className="flex gap-2 px-2 py-5">
      <span className="sr-only">Loading...</span>
      <div className="h-2 w-2 animate-[pulse_1.5s_ease-in-out_infinite] rounded-full bg-neutral-500"></div>
      <div className="h-2 w-2 animate-[pulse_1.5s_ease-in-out_infinite_0.3s] rounded-full bg-neutral-500"></div>
      <div className="h-2 w-2 animate-[pulse_1.5s_ease-in-out_infinite_0.6s] rounded-full bg-neutral-500"></div>
    </div>
  );
}
